import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Avatar,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import VerifiedIcon from "@mui/icons-material/Verified";
import BusinessIcon from "@mui/icons-material/Business";
import ScienceIcon from "@mui/icons-material/Science";
import { listCompanies, getCompanyContacts, addContact } from "../services/api";
import { trackEvent } from "../services/analytics";
import type { Company, Contact } from "../types";

const ROLE_CATEGORIES = [
  "CMC/Manufacturing",
  "Business Development",
  "Supply Chain",
  "Executive",
  "Commercial",
  "Medical/Clinical",
  "R&D Lead",
  "Finance",
];

/** Capitalize a name properly: first letter upper, rest lower. */
function capitalizeName(name: string): string {
  if (!name) return name;
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

/** Capitalize a full display name like "john smith" -> "John Smith". */
function capitalizeFullName(fullName: string): string {
  if (!fullName) return fullName;
  return fullName
    .split(" ")
    .map(capitalizeName)
    .join(" ");
}

/** Ensure a LinkedIn URL has the https:// prefix. */
function ensureLinkedInUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `https://${url}`;
}

export default function Companies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("pharma");
  const [tierFilter, setTierFilter] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Sorting
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // Add contact dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addCompanyId, setAddCompanyId] = useState<number | null>(null);
  const [newContact, setNewContact] = useState({
    first_name: "",
    last_name: "",
    title: "",
    email: "",
    phone: "",
    linkedin_url: "",
    role_category: "",
    notes: "",
  });

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listCompanies({
        search: search || undefined,
        company_type: typeFilter || undefined,
        company_tier: tierFilter || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
        page: page + 1,
        page_size: rowsPerPage,
      });
      setCompanies(data.results);
      setTotal(data.total);
      if (search || typeFilter || tierFilter) trackEvent("company_search", { search, type: typeFilter, tier: tierFilter, results: data.total });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, tierFilter, sortBy, sortDir, page, rowsPerPage]);

  useEffect(() => {
    const timer = setTimeout(fetchCompanies, 300);
    return () => clearTimeout(timer);
  }, [fetchCompanies]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir(field === "name" ? "asc" : "desc");
    }
    setPage(0);
  };

  const handleExpand = async (companyId: number) => {
    if (expandedId === companyId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(companyId);
    setContactsLoading(true);
    try {
      const data = await getCompanyContacts(companyId);
      setContacts(data.contacts);
    } catch (err) {
      console.error(err);
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const handleAddContact = async () => {
    if (!addCompanyId || !newContact.first_name || !newContact.last_name) return;
    try {
      await addContact(addCompanyId, newContact);
      setAddDialogOpen(false);
      setNewContact({
        first_name: "",
        last_name: "",
        title: "",
        email: "",
        phone: "",
        linkedin_url: "",
        role_category: "",
        notes: "",
      });
      // Refresh contacts
      if (expandedId === addCompanyId) {
        const data = await getCompanyContacts(addCompanyId);
        setContacts(data.contacts);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Group contacts by role_category for the expanded view
  const groupedContacts = contacts.reduce<Record<string, Contact[]>>((acc, c) => {
    const key = c.role_category || "Other";
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Companies & Contacts
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              label="Search Companies"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              sx={{ minWidth: 300 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Organization Type</InputLabel>
              <Select
                value={typeFilter}
                label="Organization Type"
                onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pharma">Pharma & Biotech</MenuItem>
                <MenuItem value="academic">Academic</MenuItem>
                <MenuItem value="hospital">Hospital</MenuItem>
                <MenuItem value="government">Government</MenuItem>
                <MenuItem value="research_institute">Research Institute</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Company Tier</InputLabel>
              <Select
                value={tierFilter}
                label="Company Tier"
                onChange={(e) => { setTierFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Tiers</MenuItem>
                <MenuItem value="big_pharma">Big Pharma</MenuItem>
                <MenuItem value="mid_cap">Mid-Cap</MenuItem>
                <MenuItem value="emerging">Emerging</MenuItem>
                <MenuItem value="startup">Startup</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {total} companies
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* Company Table */}
      <Card>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#fafafa" }}>
                    <TableCell width={40} />
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "name"}
                        direction={sortBy === "name" ? sortDir : "asc"}
                        onClick={() => handleSort("name")}
                      >
                        Company
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Tier</TableCell>
                    <TableCell>Employees</TableCell>
                    <TableCell>Founded</TableCell>
                    <TableCell>Ticker</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "pipeline_count"}
                        direction={sortBy === "pipeline_count" ? sortDir : "desc"}
                        onClick={() => handleSort("pipeline_count")}
                      >
                        Pipeline
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "contact_count"}
                        direction={sortBy === "contact_count" ? sortDir : "desc"}
                        onClick={() => handleSort("contact_count")}
                      >
                        Contacts
                      </TableSortLabel>
                    </TableCell>
                    <TableCell width={50} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companies.map((company) => (
                    <React.Fragment key={company.id}>
                      <TableRow
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleExpand(company.id)}
                      >
                        <TableCell>
                          <IconButton size="small">
                            {expandedId === company.id ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.light" }}>
                              <BusinessIcon fontSize="small" />
                            </Avatar>
                            <Box>
                              <Typography fontWeight={600} variant="body2">
                                {company.name}
                              </Typography>
                              {company.headquarters && (
                                <Typography variant="caption" color="text.secondary">
                                  {company.headquarters}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {company.type && (
                            <Chip label={company.type} size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          {company.company_tier && company.company_tier !== "unknown" ? (
                            <Chip
                              label={
                                company.company_tier === "big_pharma" ? "Big Pharma" :
                                company.company_tier === "mid_cap" ? "Mid-Cap" :
                                company.company_tier === "emerging" ? "Emerging" :
                                company.company_tier === "startup" ? "Startup" :
                                company.company_tier
                              }
                              size="small"
                              color={
                                company.company_tier === "big_pharma" ? "error" :
                                company.company_tier === "mid_cap" ? "warning" :
                                company.company_tier === "emerging" ? "primary" :
                                "default"
                              }
                              variant="outlined"
                            />
                          ) : "--"}
                        </TableCell>
                        <TableCell>
                          {company.employee_count ? company.employee_count.toLocaleString() : "--"}
                        </TableCell>
                        <TableCell>
                          {company.founded_year || "--"}
                        </TableCell>
                        <TableCell>{company.ticker || "--"}</TableCell>
                        <TableCell>
                          <Chip
                            icon={<ScienceIcon sx={{ fontSize: 14 }} />}
                            label={company.pipeline_count}
                            size="small"
                            color={company.pipeline_count > 0 ? "primary" : "default"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={company.contact_count}
                            size="small"
                            color={company.contact_count > 0 ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAddCompanyId(company.id);
                              setAddDialogOpen(true);
                            }}
                          >
                            <PersonAddIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      {/* Expanded Contacts */}
                      <TableRow>
                        <TableCell colSpan={10} sx={{ py: 0, border: 0 }}>
                          <Collapse
                            in={expandedId === company.id}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ py: 2, px: 4, backgroundColor: "#fafafa" }}>
                              {contactsLoading ? (
                                <CircularProgress size={24} />
                              ) : contacts.length === 0 ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  No contacts on file.{" "}
                                  <Link
                                    component="button"
                                    onClick={() => {
                                      setAddCompanyId(company.id);
                                      setAddDialogOpen(true);
                                    }}
                                  >
                                    Add one
                                  </Link>
                                </Typography>
                              ) : (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                                  {Object.entries(groupedContacts).map(([category, categoryContacts]) => (
                                    <Card
                                      key={category}
                                      variant="outlined"
                                      sx={{ minWidth: 280, maxWidth: 400, flex: "1 1 300px" }}
                                    >
                                      <CardContent sx={{ pb: "12px !important" }}>
                                        <Typography
                                          variant="subtitle2"
                                          color="primary"
                                          sx={{ mb: 1, fontWeight: 700 }}
                                        >
                                          {category}
                                        </Typography>
                                        {categoryContacts.map((c, idx) => (
                                          <Box
                                            key={idx}
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "space-between",
                                              py: 0.75,
                                              borderBottom: idx < categoryContacts.length - 1 ? "1px solid #eee" : "none",
                                            }}
                                          >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                              <Avatar
                                                sx={{
                                                  width: 28,
                                                  height: 28,
                                                  bgcolor: "secondary.light",
                                                  fontSize: 12,
                                                }}
                                              >
                                                <PersonIcon sx={{ fontSize: 16 }} />
                                              </Avatar>
                                              <Box>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                  <Typography fontWeight={600} variant="body2">
                                                    {capitalizeFullName(c.name)}
                                                  </Typography>
                                                  {c.verified && (
                                                    <VerifiedIcon
                                                      sx={{
                                                        fontSize: 14,
                                                        color: "success.main",
                                                      }}
                                                    />
                                                  )}
                                                </Box>
                                                {c.title && (
                                                  <Typography variant="caption" color="text.secondary">
                                                    {c.title}
                                                  </Typography>
                                                )}
                                              </Box>
                                            </Box>
                                            <Box sx={{ display: "flex", gap: 0.5 }}>
                                              {c.email && (
                                                <IconButton
                                                  size="small"
                                                  href={`mailto:${c.email}`}
                                                  component="a"
                                                >
                                                  <EmailIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                              )}
                                              {c.phone && (
                                                <IconButton
                                                  size="small"
                                                  href={`tel:${c.phone}`}
                                                  component="a"
                                                >
                                                  <PhoneIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                              )}
                                              {c.linkedin && (
                                                <IconButton
                                                  size="small"
                                                  href={ensureLinkedInUrl(c.linkedin)!}
                                                  target="_blank"
                                                  rel="noopener"
                                                  component="a"
                                                >
                                                  <LinkedInIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                              )}
                                            </Box>
                                          </Box>
                                        ))}
                                      </CardContent>
                                    </Card>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Card>

      {/* Add Contact Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Contact</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="First Name"
                required
                fullWidth
                value={newContact.first_name}
                onChange={(e) =>
                  setNewContact({ ...newContact, first_name: e.target.value })
                }
              />
              <TextField
                label="Last Name"
                required
                fullWidth
                value={newContact.last_name}
                onChange={(e) =>
                  setNewContact({ ...newContact, last_name: e.target.value })
                }
              />
            </Stack>
            <TextField
              label="Title"
              fullWidth
              value={newContact.title}
              onChange={(e) =>
                setNewContact({ ...newContact, title: e.target.value })
              }
            />
            <FormControl fullWidth>
              <InputLabel>Role Category</InputLabel>
              <Select
                value={newContact.role_category}
                label="Role Category"
                onChange={(e) =>
                  setNewContact({ ...newContact, role_category: e.target.value })
                }
              >
                {ROLE_CATEGORIES.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={newContact.email}
              onChange={(e) =>
                setNewContact({ ...newContact, email: e.target.value })
              }
            />
            <TextField
              label="Phone"
              fullWidth
              value={newContact.phone}
              onChange={(e) =>
                setNewContact({ ...newContact, phone: e.target.value })
              }
            />
            <TextField
              label="LinkedIn URL"
              fullWidth
              value={newContact.linkedin_url}
              onChange={(e) =>
                setNewContact({ ...newContact, linkedin_url: e.target.value })
              }
            />
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={2}
              value={newContact.notes}
              onChange={(e) =>
                setNewContact({ ...newContact, notes: e.target.value })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddContact}
            disabled={!newContact.first_name || !newContact.last_name}
          >
            Add Contact
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
